import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  InputAdornment,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import { api } from '../api';
import { Customer } from '../types';

interface CustomerDialogProps {
  open: boolean;
  initialName?: string;
  initialPhone?: string;
  initialAddress?: string;
  initialCCCD?: string;
  initialCustomerType?: string;
  onClose: () => void;
  onSubmit: (id: string | null, name: string, phone: string, address: string, cccd: string, customerType: string, isChanged: boolean) => void;
}

export const CustomerDialog: React.FC<CustomerDialogProps> = ({
  open,
  initialName = '',
  initialPhone = '',
  initialAddress = '',
  initialCCCD = '',
  initialCustomerType = 'Retail',
  onClose,
  onSubmit,
}) => {
  const [name, setName] = React.useState(initialName);
  const [phone, setPhone] = React.useState(initialPhone);
  const [address, setAddress] = React.useState(initialAddress);
  const [cccd, setCCCD] = React.useState(initialCCCD);
  const [customerType, setCustomerType] = React.useState(initialCustomerType);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [foundCustomer, setFoundCustomer] = React.useState(false);
  const [id, setId] = React.useState<string | null>(null);
  const [editingField, setEditingField] = React.useState<'name' | 'cccd' | 'address' | null>(null);
  const phoneInputRef = React.useRef<HTMLInputElement>(null);
  const cccdInputRef = React.useRef<HTMLInputElement>(null);
  const nameInputRef = React.useRef<HTMLInputElement>(null);
  const addressInputRef = React.useRef<HTMLInputElement>(null);
  const submitButtonRef = React.useRef<HTMLButtonElement>(null);
  const [customer, setCustomer] = React.useState<Customer | null>(null);

  // Reset form when dialog opens with new initial values
  React.useEffect(() => {
    if (open) {
      setName(initialName);
      setPhone(initialPhone);
      setAddress(initialAddress);
      setCCCD(initialCCCD);
      setCustomerType(initialCustomerType);
      setError('');
      // Focus phone input after dialog animation
      setTimeout(() => {
        phoneInputRef.current?.focus();
      }, 100);
    }
  }, [open, initialName, initialPhone, initialAddress, initialCCCD, initialCustomerType]);

  const handleSubmit = () => {
    if (!phone.trim()) {
      setError('Vui lòng nhập số điện thoại');
      return;
    }
    const isChanged = customer
      ? customer.name !== name.trim() || customer.address !== address.trim() ||
        (customer.cccd || '') !== cccd.trim() ||
        (customer.customerType || 'Retail') !== customerType
      : true;

    onSubmit(id, name.trim(), phone.trim(), address.trim(), cccd.trim(), customerType, isChanged);
  };

  const handleSearchCustomer = async () => {
    if (!phone.trim()) {
      setError('Vui lòng nhập số điện thoại');
      return;
    }

    setLoading(true);
    try {
      const customer = await api.getCustomerByPhone(phone);
      if (customer) {
        // Found existing customer
        setCustomer(customer);
        setId(customer.id);
        setName(customer.name);
        setAddress(customer.address);
        setCCCD(customer.cccd || '');
        setCustomerType(customer.customerType || 'Retail');
        setFoundCustomer(true);
        setEditingField(null);
        setTimeout(() => {
          submitButtonRef.current?.focus();
        }, 100);
        setError('');
      } else {
        // Customer not found - move to name input
        setCustomer(null);
        setId(null);
        setName('');
        setAddress('');
        setCCCD('');
        setFoundCustomer(false);
        setEditingField(null);
        setError('Không tìm thấy khách hàng. Vui lòng nhập thông tin mới.');
        setTimeout(() => {
          nameInputRef.current?.focus();
        }, 100);
      }
    } catch (err) {
      console.error('Error searching customer:', err);
      setError('Lỗi khi tìm kiếm khách hàng');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, field: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (field === 'phone' && phone) {
        handleSearchCustomer();
      } else if (field === 'name') {
        document.getElementById('cccd-input')?.focus();
      } else if (field === 'cccd') {
        addressInputRef.current?.focus();
      } else if (field === 'address') {
        handleSubmit();
      }
    }
  };

  const handleClose = () => {
    setName('');
    setPhone('');
    setAddress('');
    setCCCD('');
    setError('');
    setFoundCustomer(false);
    setEditingField(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>👨🏻‍💼 Chọn khách hàng</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              inputRef={phoneInputRef}
              fullWidth
              label="Số điện thoại"
              placeholder="Nhập số điện thoại..."
              value={phone}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const cleanValue = e.target.value.replace(/\D/g, '').slice(0, 10);
                setPhone(cleanValue);
                setError('');
              }}
              onKeyPress={(e) => handleKeyPress(e, 'phone')}
              inputProps={{ maxLength: 10 }}
              required
              disabled={loading}
            />
            <FormControl fullWidth disabled={loading}>
              <InputLabel>Loại khách hàng</InputLabel>
              <Select
                value={customerType}
                label="Loại khách hàng"
                onChange={(e) => setCustomerType(e.target.value)}
              >
                <MenuItem value="Retail">Khách lẻ</MenuItem>
                <MenuItem value="Wholesale">Thương Lái</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              fullWidth
              inputRef={nameInputRef}
              id="name-input"
              label="Tên khách hàng"
              placeholder="Nhập tên khách hàng..."
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, 'name')}
              disabled={loading || (foundCustomer && editingField !== 'name')}
              InputProps={
                foundCustomer ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setEditingField(editingField === 'name' ? null : 'name')}
                        disabled={loading}
                        edge="end"
                      >
                        {editingField === 'name' ? <CheckIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                } : undefined
              }
            />
            <TextField
              fullWidth
              inputRef={cccdInputRef}
              id="cccd-input"
              label="Số CCCD"
              placeholder="Nhập số CCCD..."
              value={cccd}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const cleanValue = e.target.value.replace(/\D/g, '').slice(0, 12);
                setCCCD(cleanValue);
              }}
              onKeyPress={(e) => handleKeyPress(e, 'cccd')}
              inputProps={{ maxLength: 12 }}
              disabled={loading || (foundCustomer && editingField !== 'cccd')}
              InputProps={
                foundCustomer ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setEditingField(editingField === 'cccd' ? null : 'cccd')}
                        disabled={loading}
                        edge="end"
                      >
                        {editingField === 'cccd' ? <CheckIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                } : undefined
              }
            />
          </Box>
          <TextField
            fullWidth
            id="address-input"
            label="Địa chỉ"
            placeholder="Nhập địa chỉ..."
            value={address}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddress(e.target.value)}
            onKeyPress={(e) => handleKeyPress(e, 'address')}
            multiline
            rows={2}
            inputRef={addressInputRef}
            disabled={loading || (foundCustomer && editingField !== 'address')}
            InputProps={
              foundCustomer ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setEditingField(editingField === 'address' ? null : 'address')}
                      disabled={loading}
                      edge="end"
                    >
                      {editingField === 'address' ? <CheckIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              } : undefined
            }
          />
          {error && (
            <Box sx={{ color: 'error.main', fontSize: '0.875rem' }}>
              {error}
            </Box>
          )}
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={20} />
              <span>Đang tìm kiếm thông tin khách hàng...</span>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Hủy
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary" 
          disabled={loading} 
          ref={submitButtonRef}
          sx={{
            '&:focus': {
              backgroundColor: 'info.dark',
              boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.5)',
            },
          }}
        >
          Tiếp tục
        </Button>
      </DialogActions>
    </Dialog>
  );
};
